import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpRightFromSquare,
  faBell,
  faCheck,
  faCheckDouble,
  faTrashCan,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import { useNotifications, type Notification } from '../contexts/NotificationContext'

function formatRelativeTime(date: string): string {
  const then = new Date(date).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.round((Date.now() - then) / 1000)
  if (seconds < 45) return 'Just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(date).toLocaleDateString()
}

/** Don't bubble to a host overlay (which would close the menu). */
function stopMenuBubble(e: React.SyntheticEvent) {
  e.stopPropagation()
}

/** Prevent focus moving off the menu so the list doesn't collapse. */
function preventFocusSteal(e: React.MouseEvent) {
  e.preventDefault()
}

const SWIPE_COMMIT = 64
const SWIPE_MAX = 112
const SWIPE_AXIS_SLOP = 12
/** Prefer horizontal once dx is clearly competing with dy (allows diagonal drift). */
const SWIPE_H_BIAS = 0.55
const SWIPE_FLICK_VELOCITY = 0.45 // px/ms
const SWIPE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

type SwipePhase = 'idle' | 'dragging' | 'returning' | 'completing-read' | 'completing-remove'

type NotificationRowProps = {
  notification: Notification
  onOpen: (n: Notification) => void
  onMarkRead: (id: string) => void
  onDismiss: (id: string) => void
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  notification: n,
  onOpen,
  onMarkRead,
  onDismiss,
}) => {
  const canOpen = Boolean(n.url)
  const canRead = !n.read
  const canRemove = n.dismissible !== false

  const rowRef = React.useRef<HTMLDivElement>(null)
  const slideRef = React.useRef<HTMLDivElement>(null)
  const [offsetX, setOffsetX] = React.useState(0)
  const [phase, setPhase] = React.useState<SwipePhase>('idle')
  const [axisLock, setAxisLock] = React.useState<'undecided' | 'h' | 'v'>('undecided')
  const [rowHeight, setRowHeight] = React.useState<number | null>(null)
  const [exiting, setExiting] = React.useState(false)

  const startRef = React.useRef<{ x: number; y: number; offset: number; pointerId: number } | null>(
    null,
  )
  const offsetRef = React.useRef(0)
  const velocityRef = React.useRef({ x: 0, t: 0, vx: 0 })
  const phaseRef = React.useRef<SwipePhase>('idle')
  const axisLockRef = React.useRef<'undecided' | 'h' | 'v'>('undecided')
  const commitTimerRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    offsetRef.current = offsetX
  }, [offsetX])

  React.useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  React.useEffect(() => {
    axisLockRef.current = axisLock
  }, [axisLock])

  React.useEffect(() => {
    return () => {
      if (commitTimerRef.current != null) window.clearTimeout(commitTimerRef.current)
    }
  }, [])

  // Non-passive touchmove so we can preventDefault once horizontal is locked (iOS).
  React.useEffect(() => {
    const el = slideRef.current
    if (!el || axisLock !== 'h') return
    const blockScroll = (e: TouchEvent) => {
      e.preventDefault()
    }
    el.addEventListener('touchmove', blockScroll, { passive: false })
    return () => el.removeEventListener('touchmove', blockScroll)
  }, [axisLock])

  const clearCommitTimer = () => {
    if (commitTimerRef.current != null) {
      window.clearTimeout(commitTimerRef.current)
      commitTimerRef.current = null
    }
  }

  const releasePointer = (el: HTMLElement, pointerId: number) => {
    try {
      if (el.hasPointerCapture?.(pointerId)) el.releasePointerCapture(pointerId)
    } catch {
      /* ignore */
    }
  }

  const snapBack = React.useCallback(() => {
    clearCommitTimer()
    startRef.current = null
    axisLockRef.current = 'undecided'
    setAxisLock('undecided')
    setPhase('returning')
    setOffsetX(0)
    commitTimerRef.current = window.setTimeout(() => {
      setPhase('idle')
      commitTimerRef.current = null
    }, 280)
  }, [])

  const finishRemove = React.useCallback(() => {
    const height = rowRef.current?.getBoundingClientRect().height ?? 0
    setRowHeight(height)
    requestAnimationFrame(() => {
      setExiting(true)
      commitTimerRef.current = window.setTimeout(() => {
        onDismiss(n._id)
        commitTimerRef.current = null
      }, 280)
    })
  }, [n._id, onDismiss])

  const commitRead = React.useCallback(() => {
    clearCommitTimer()
    startRef.current = null
    axisLockRef.current = 'undecided'
    setAxisLock('undecided')
    setPhase('completing-read')
    setOffsetX(SWIPE_MAX)
    onMarkRead(n._id)
    commitTimerRef.current = window.setTimeout(() => {
      setOffsetX(0)
      setPhase('returning')
      commitTimerRef.current = window.setTimeout(() => {
        setPhase('idle')
        commitTimerRef.current = null
      }, 260)
    }, 160)
  }, [n._id, onMarkRead])

  const commitRemove = React.useCallback(() => {
    clearCommitTimer()
    startRef.current = null
    axisLockRef.current = 'undecided'
    setAxisLock('undecided')
    const width = rowRef.current?.getBoundingClientRect().width ?? 360
    setPhase('completing-remove')
    setOffsetX(-(width + 24))
    finishRemove()
  }, [finishRemove])

  const settleFromGesture = React.useCallback(
    (x: number, vx: number) => {
      const flickedRead = canRead && (x > SWIPE_COMMIT || (x > 28 && vx > SWIPE_FLICK_VELOCITY))
      const flickedRemove =
        canRemove && (x < -SWIPE_COMMIT || (x < -28 && vx < -SWIPE_FLICK_VELOCITY))

      if (flickedRead) {
        commitRead()
      } else if (flickedRemove) {
        commitRemove()
      } else {
        snapBack()
      }
    },
    [canRead, canRemove, commitRead, commitRemove, snapBack],
  )

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    if (e.pointerType === 'mouse') return
    if (phaseRef.current === 'completing-read' || phaseRef.current === 'completing-remove') return
    if (exiting) return

    clearCommitTimer()
    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      offset: offsetRef.current,
      pointerId: e.pointerId,
    }
    axisLockRef.current = 'undecided'
    setAxisLock('undecided')
    velocityRef.current = { x: e.clientX, t: e.timeStamp, vx: 0 }
    setPhase('dragging')
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const start = startRef.current
    if (!start || start.pointerId !== e.pointerId) return
    if (phaseRef.current !== 'dragging') return

    const dx = e.clientX - start.x
    const dy = e.clientY - start.y

    if (axisLockRef.current === 'undecided') {
      const adx = Math.abs(dx)
      const ady = Math.abs(dy)
      if (adx < SWIPE_AXIS_SLOP && ady < SWIPE_AXIS_SLOP) return

      // Lock horizontal unless vertical clearly dominates
      if (adx >= ady * SWIPE_H_BIAS && adx >= SWIPE_AXIS_SLOP * 0.75) {
        axisLockRef.current = 'h'
        setAxisLock('h')
      } else if (ady > adx) {
        axisLockRef.current = 'v'
        setAxisLock('v')
        startRef.current = null
        setPhase('idle')
        setOffsetX(0)
        releasePointer(e.currentTarget as HTMLElement, e.pointerId)
        return
      } else {
        return
      }
    }

    if (axisLockRef.current !== 'h') return

    e.preventDefault()
    e.stopPropagation()

    const prev = velocityRef.current
    const dt = Math.max(1, e.timeStamp - prev.t)
    velocityRef.current = {
      x: e.clientX,
      t: e.timeStamp,
      vx: (e.clientX - prev.x) / dt,
    }

    let next = start.offset + dx
    const min = canRemove ? -SWIPE_MAX : 0
    const max = canRead ? SWIPE_MAX : 0
    if (next > max) next = max + (next - max) * 0.2
    if (next < min) next = min + (next - min) * 0.2
    setOffsetX(next)
  }

  const endGesture = (e: React.PointerEvent) => {
    const start = startRef.current
    if (!start || start.pointerId !== e.pointerId) return
    releasePointer(e.currentTarget as HTMLElement, e.pointerId)

    const wasHorizontal = axisLockRef.current === 'h'
    const x = offsetRef.current
    const vx = velocityRef.current.vx
    startRef.current = null
    axisLockRef.current = 'undecided'
    setAxisLock('undecided')

    if (wasHorizontal) {
      settleFromGesture(x, vx)
    } else {
      setPhase('idle')
      setOffsetX(0)
    }
  }

  const onPointerCancel = (e: React.PointerEvent) => {
    // If we already locked horizontal, settle instead of hard-resetting (iOS often cancels).
    const start = startRef.current
    if (start && start.pointerId === e.pointerId && axisLockRef.current === 'h') {
      endGesture(e)
      return
    }
    startRef.current = null
    axisLockRef.current = 'undecided'
    setAxisLock('undecided')
    if (phaseRef.current === 'dragging') snapBack()
  }

  const showReadReveal = (offsetX > 6 && canRead) || phase === 'completing-read'
  const showRemoveReveal = (offsetX < -6 && canRemove) || phase === 'completing-remove'
  const lockedHorizontal = axisLock === 'h'
  const animating =
    phase === 'returning' || phase === 'completing-read' || phase === 'completing-remove'

  return (
    <div
      ref={rowRef}
      className="relative overflow-hidden border-b border-base-200 last:border-0"
      style={{
        height: rowHeight != null ? (exiting ? 0 : rowHeight) : undefined,
        opacity: exiting ? 0 : 1,
        transition: exiting
          ? `height 260ms ${SWIPE_EASE}, opacity 200ms ease, border-width 260ms ${SWIPE_EASE}`
          : undefined,
        borderBottomWidth: exiting ? 0 : undefined,
        touchAction: lockedHorizontal ? 'none' : 'pan-y',
      }}
    >
      {/* Full-bleed action reveals */}
      <div className="absolute inset-0" aria-hidden>
        <div
          className={`absolute inset-0 flex items-center justify-start gap-2 bg-success px-4 text-success-content transition-opacity duration-150 ${
            showReadReveal ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <FontAwesomeIcon
            icon={faCheck}
            className={`transition-transform duration-150 ${
              offsetX > SWIPE_COMMIT ? 'scale-125' : 'scale-100'
            }`}
          />
          <span className="text-sm font-semibold">Read</span>
        </div>
        <div
          className={`absolute inset-0 flex items-center justify-end gap-2 bg-error px-4 text-error-content transition-opacity duration-150 ${
            showRemoveReveal ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span className="text-sm font-semibold">Remove</span>
          <FontAwesomeIcon
            icon={faTrashCan}
            className={`transition-transform duration-150 ${
              offsetX < -SWIPE_COMMIT ? 'scale-125' : 'scale-100'
            }`}
          />
        </div>
      </div>

      <div
        ref={slideRef}
        className={`relative bg-base-100 ${!n.read ? 'bg-base-200/50' : ''}`}
        style={{
          transform: `translate3d(${offsetX}px, 0, 0)`,
          transition: animating ? `transform 280ms ${SWIPE_EASE}` : 'none',
          willChange: phase === 'dragging' || animating ? 'transform' : undefined,
          touchAction: lockedHorizontal ? 'none' : 'pan-y',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endGesture}
        onPointerCancel={onPointerCancel}
      >
        <div className="px-3 pt-2.5 pb-2">
          <div className="flex items-start gap-2">
            <span
              className={`mt-1.5 size-2 shrink-0 rounded-full ${
                !n.read ? 'bg-primary' : 'bg-transparent'
              }`}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2">
                <p
                  className={`min-w-0 flex-1 truncate text-sm ${
                    !n.read ? 'font-semibold' : 'font-medium'
                  }`}
                >
                  {n.title}
                </p>
                <span className="shrink-0 text-[11px] tabular-nums text-base-content/45">
                  {formatRelativeTime(n.date)}
                </span>
              </div>
              <p className="line-clamp-3 whitespace-normal break-words text-xs text-base-content/75">
                {n.message}
              </p>
            </div>
          </div>

          {(canOpen || canRead || canRemove) && (
            <div className="mt-2 flex flex-wrap gap-1.5 pl-4">
              {canOpen && (
                <button
                  type="button"
                  className="btn btn-primary btn-xs gap-1"
                  aria-label={`Open ${n.title}`}
                  title="Open linked page"
                  onMouseDown={preventFocusSteal}
                  onPointerDown={stopMenuBubble}
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpen(n)
                  }}
                >
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  Open
                </button>
              )}
              {canRead && (
                <button
                  type="button"
                  className="btn btn-ghost btn-xs gap-1"
                  aria-label="Mark as read"
                  title="Mark as read"
                  onMouseDown={preventFocusSteal}
                  onPointerDown={stopMenuBubble}
                  onClick={(e) => {
                    e.stopPropagation()
                    onMarkRead(n._id)
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  Read
                </button>
              )}
              {canRemove && (
                <button
                  type="button"
                  className="btn btn-ghost btn-xs gap-1 text-error"
                  aria-label="Remove notification"
                  title="Remove"
                  onMouseDown={preventFocusSteal}
                  onPointerDown={stopMenuBubble}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDismiss(n._id)
                  }}
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface NotificationsPanelProps {
  onClose?: () => void
  /** Called after navigating away from a notification (or handling activation). */
  onItemActivate?: () => void
  maxItems?: number
  showClose?: boolean
  className?: string
  listClassName?: string
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  onClose,
  onItemActivate,
  maxItems = 20,
  showClose = true,
  className = '',
  listClassName = 'max-h-80',
}) => {
  const navigate = useNavigate()
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    dismissAllNotifications,
    unreadCount,
    loading,
  } = useNotifications()

  const visible = notifications.slice(0, maxItems)
  const dismissibleCount = notifications.filter((n) => n.dismissible !== false).length
  const busyRef = React.useRef(false)
  const [actionBusy, setActionBusy] = React.useState(false)
  const [confirmClear, setConfirmClear] = React.useState(false)

  React.useEffect(() => {
    if (dismissibleCount === 0) setConfirmClear(false)
  }, [dismissibleCount])

  const runAction = async (fn: () => Promise<void>) => {
    if (busyRef.current) return
    busyRef.current = true
    setActionBusy(true)
    try {
      await fn()
    } finally {
      busyRef.current = false
      setActionBusy(false)
    }
  }

  const handleOpen = async (notification: Notification) => {
    await markAsRead(notification._id)
    if (notification.url) {
      navigate(notification.url)
    }
    onItemActivate?.()
  }

  const handleMarkAllRead = () => {
    if (unreadCount === 0) return
    void runAction(markAllAsRead)
  }

  const handleDismissAll = () => {
    if (dismissibleCount === 0) return
    setConfirmClear(true)
  }

  const handleConfirmClear = () => {
    setConfirmClear(false)
    void runAction(dismissAllNotifications)
  }

  return (
    <div className={`flex min-h-0 flex-col bg-base-100 ${className}`}>
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-base-200 px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Notifications</p>
          <p className="text-[11px] text-base-content/50">
            {loading && notifications.length === 0
              ? 'Loading…'
              : unreadCount > 0
                ? `${unreadCount} unread`
                : notifications.length > 0
                  ? 'All caught up'
                  : 'No notifications'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            className="btn btn-ghost btn-xs gap-1"
            disabled={actionBusy || unreadCount === 0 || confirmClear}
            title="Mark all as read"
            aria-label="Mark all as read"
            onMouseDown={preventFocusSteal}
            onPointerDown={stopMenuBubble}
            onClick={handleMarkAllRead}
          >
            <FontAwesomeIcon icon={faCheckDouble} />
            <span>Read all</span>
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-xs gap-1"
            disabled={actionBusy || dismissibleCount === 0 || confirmClear}
            title="Clear all"
            aria-label="Clear all notifications"
            onMouseDown={preventFocusSteal}
            onPointerDown={stopMenuBubble}
            onClick={handleDismissAll}
          >
            <FontAwesomeIcon icon={faTrashCan} />
            <span>Clear all</span>
          </button>
          {showClose && onClose && (
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-square"
              aria-label="Close notifications"
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>

      {confirmClear && (
        <div
          className="shrink-0 border-b border-base-200 bg-base-200/70 px-3 py-2"
          role="alertdialog"
          aria-labelledby="notif-clear-title"
          aria-describedby="notif-clear-desc"
        >
          <p id="notif-clear-title" className="text-sm font-medium">
            Clear {dismissibleCount} notification{dismissibleCount === 1 ? '' : 's'}?
          </p>
          <p id="notif-clear-desc" className="mt-0.5 text-[11px] text-base-content/60">
            They will be removed from your list. This cannot be undone.
          </p>
          <div className="mt-2 flex justify-end gap-1">
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              disabled={actionBusy}
              onMouseDown={preventFocusSteal}
              onPointerDown={stopMenuBubble}
              onClick={() => setConfirmClear(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-error btn-xs gap-1"
              disabled={actionBusy || dismissibleCount === 0}
              onMouseDown={preventFocusSteal}
              onPointerDown={stopMenuBubble}
              onClick={handleConfirmClear}
            >
              <FontAwesomeIcon icon={faTrashCan} />
              Clear
            </button>
          </div>
        </div>
      )}

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-y-contain ${listClassName}`}
      >
        {visible.length > 0 ? (
          visible.map((n) => (
            <NotificationRow
              key={n._id}
              notification={n}
              onOpen={(item) => void handleOpen(item)}
              onMarkRead={(id) => void markAsRead(id)}
              onDismiss={(id) => void dismissNotification(id)}
            />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
            <FontAwesomeIcon icon={faBell} className="text-2xl text-base-content/25" />
            <p className="text-sm font-medium text-base-content/70">No notifications</p>
            <p className="text-xs text-base-content/50">New alerts will show up here.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPanel
